import {
	NavigateFunction, NavigateOptions, To, useLocation, useNavigate,
} from 'react-router-dom';

import { parseQuery } from '@common/typescript/utils/url';
import { Mode, NumberKeyedDictionary } from '@common/react/objects/Core';

import { UserGroupType } from '@commonTuna/react/objects/UserGroup';
import { PageType } from '@commonTuna/react/objects/Page';

import { useUserGroupContext } from '@app/components/UI/UserGroupProvider/UserGroupProvider';
import { DayOffFilters } from '@app/objects/Filters';

export enum PatientNoteTabs {
	Patient = 0,
	Appointment = 1,
	Inquiries = 2,
}

export const PatientNoteTabsKeys = {
	[PatientNoteTabs.Patient]: 'patient',
	[PatientNoteTabs.Appointment]: 'appointment',
	[PatientNoteTabs.Inquiries]: 'inquiries',
};

export enum PatientTabs {
	General = 0,
	DrivingLicense = 1,
	Insurance = 2,
	Cards = 3,
	Pharmacies = 4,
	Appointments = 5,
	Orders = 6,
	Bills = 7,
	Allergies = 8,
	Vitals = 9,
	Analyzes = 10,
	Prescriptions = 11,
	Documents = 12,
	Nopali = 13,
	Questions = 14,
	Disclosures = 15,
	Communication = 16,
	Photos = 17,
	Notifications = 18,
	Emails = 19,
	Faxes = 20,
	Sms = 21,
}

export const PatientTabsKeys = {
	[PatientTabs.General]: 'general',
	[PatientTabs.DrivingLicense]: 'drivingLicense',
	[PatientTabs.Insurance]: 'insurance',
	[PatientTabs.Pharmacies]: 'pharmacies',
	[PatientTabs.Cards]: 'cards',
	[PatientTabs.Appointments]: 'appointments',
	[PatientTabs.Orders]: 'orders',
	[PatientTabs.Bills]: 'bills',
	[PatientTabs.Allergies]: 'allergies',
	[PatientTabs.Vitals]: 'vitals',
	[PatientTabs.Analyzes]: 'analyzes',
	[PatientTabs.Prescriptions]: 'prescriptions',
	[PatientTabs.Documents]: 'documents',
	[PatientTabs.Nopali]: 'nopali',
	[PatientTabs.Questions]: 'questions',
	[PatientTabs.Disclosures]: 'disclosures',
	[PatientTabs.Communication]: 'communication',
	[PatientTabs.Photos]: 'photos',
	[PatientTabs.Notifications]: 'notifications',
	[PatientTabs.Emails]: 'emails',
	[PatientTabs.Faxes]: 'faxes',
	[PatientTabs.Sms]: 'sms',
};

export enum PatientTabsGroup {
	General = 0,
	Appointments = 1,
	Medical = 2,
	Documents = 3,
	Correspondence = 4,
}

export const PatientTabsGroupKeys = {
	[PatientTabsGroup.General]: 'general-group',
	[PatientTabsGroup.Appointments]: 'appointments-group',
	[PatientTabsGroup.Medical]: 'medical-group',
	[PatientTabsGroup.Documents]: 'documents-group',
	[PatientTabsGroup.Correspondence]: 'correspondence-group',
};

export const PatientTabsGroupInnerKeys = {
	[PatientTabsGroup.General]: 'patient-tabs-general',
	[PatientTabsGroup.Appointments]: 'patient-tabs-appointments',
	[PatientTabsGroup.Medical]: 'patient-tabs-medical',
	[PatientTabsGroup.Documents]: 'patient-tabs-documents',
	[PatientTabsGroup.Correspondence]: 'patient-tabs-correspondence',
};

export const PatientTabGroup = {
	[PatientTabs.General]: PatientTabsGroup.General,
	[PatientTabs.DrivingLicense]: PatientTabsGroup.General,
	[PatientTabs.Insurance]: PatientTabsGroup.General,
	[PatientTabs.Pharmacies]: PatientTabsGroup.General,
	[PatientTabs.Cards]: PatientTabsGroup.General,
	[PatientTabs.Appointments]: PatientTabsGroup.Appointments,
	[PatientTabs.Orders]: PatientTabsGroup.Appointments,
	[PatientTabs.Bills]: PatientTabsGroup.Appointments,
	[PatientTabs.Allergies]: PatientTabsGroup.Medical,
	[PatientTabs.Vitals]: PatientTabsGroup.Medical,
	[PatientTabs.Analyzes]: PatientTabsGroup.Medical,
	[PatientTabs.Prescriptions]: PatientTabsGroup.Medical,
	[PatientTabs.Documents]: PatientTabsGroup.Documents,
	[PatientTabs.Nopali]: PatientTabsGroup.Documents,
	[PatientTabs.Questions]: PatientTabsGroup.Documents,
	[PatientTabs.Disclosures]: PatientTabsGroup.Documents,
	[PatientTabs.Communication]: PatientTabsGroup.Medical,
	[PatientTabs.Photos]: PatientTabsGroup.Documents,
	[PatientTabs.Notifications]: PatientTabsGroup.Correspondence,
	[PatientTabs.Emails]: PatientTabsGroup.Correspondence,
	[PatientTabs.Faxes]: PatientTabsGroup.Correspondence,
	[PatientTabs.Sms]: PatientTabsGroup.Correspondence,
};

export enum AppointmentTabs {
	General = 0,
	Payments = 1,
	Invite = 2,
	Vitals = 3,
	Notes = 4,
	Photos = 5,
	Documents = 6,
	Medication = 7,
	Analyzes = 8,
	History = 9,
}

export const AppointmentTabsKeys = {
	[AppointmentTabs.General]: 'general',
	[AppointmentTabs.Payments]: 'payments',
	[AppointmentTabs.Invite]: 'invite',
	[AppointmentTabs.Vitals]: 'vitals',
	[AppointmentTabs.Notes]: 'notes',
	[AppointmentTabs.Photos]: 'photos',
	[AppointmentTabs.Documents]: 'documents',
	[AppointmentTabs.Medication]: 'medication',
	[AppointmentTabs.Analyzes]: 'analyzes',
	[AppointmentTabs.History]: 'history',
};

export enum AnalysisTabs {
	General = 0,
	Files = 1,
	Results = 2,
}

export const AnalysisTabsKeys = {
	[AnalysisTabs.General]: 'general',
	[AnalysisTabs.Files]: 'files',
	[AnalysisTabs.Results]: 'results',
};

export enum UserTabs {
	General = 0,
	TwoFA = 1,
	WorkShifts = 2,
	DayOff = 3,
	Notification = 4,
	CustomNotification = 5,
	Settings = 7,
	Documents = 8,
}

export const UserTabsKeys = {
	[UserTabs.General]: 'general',
	[UserTabs.TwoFA]: 'twoFA',
	[UserTabs.WorkShifts]: 'work-shifts',
	[UserTabs.DayOff]: 'day-offs',
	[UserTabs.Notification]: 'notification',
	[UserTabs.CustomNotification]: 'customNotification',
	[UserTabs.Settings]: 'settings',
	[UserTabs.Documents]: 'documents',
};

export enum LocationTabs {
	General = 0,
	Users = 1,
	DayOff = 2,
	Rooms = 3,
	WorkingHours = 4,
	WasteProducts = 5,
	Finances = 6,
	Site = 7,
	Portal = 8,
	Laboratories = 9,
	Settings = 10,
	Researches = 11,
	ServiceHours = 12,
	//
	WorkingHoursGeneral = 13,
	WorkingHoursCustom = 14,
}

export const LocationTabsKeys = {
	[LocationTabs.General]: 'general',
	[LocationTabs.Users]: 'users',
	[LocationTabs.DayOff]: 'day-offs',
	[LocationTabs.Rooms]: 'rooms',
	[LocationTabs.WorkingHours]: 'working-hours',
	[LocationTabs.WasteProducts]: 'waste-products',
	[LocationTabs.Finances]: 'finances',
	[LocationTabs.Site]: 'site',
	[LocationTabs.Portal]: 'portal',
	[LocationTabs.Laboratories]: 'laboratories',
	[LocationTabs.Settings]: 'settings',
	[LocationTabs.Researches]: 'researches',
	[LocationTabs.ServiceHours]: 'service-hours',

	[LocationTabs.WorkingHoursGeneral]: 'general',
	[LocationTabs.WorkingHoursCustom]: 'custom',
};

export enum LocationTabsGroup {
	WorkingHours = 0,
}

export const LocationTabsGroupKeys = {
	[LocationTabsGroup.WorkingHours]: 'working-hours',
};

export const LocationTabsGroupInnerKeys = {
	[LocationTabsGroup.WorkingHours]: 'working-hours-tab',
};

export const LocationTabGroup = {
	[LocationTabs.WorkingHoursGeneral]: LocationTabsGroup.WorkingHours,
	[LocationTabs.WorkingHoursCustom]: LocationTabsGroup.WorkingHours,
};

export enum PageTabs {
	General = 0,
	SubPages = 1,
	Images = 2,
	Videos = 3,
	Faqs = 4,
	Cases = 5,
	Reviews = 6,
	Specials = 7,
	Doctors = 8,
	Menu = 9,
	DoctorsMenu = 10,
}

export const PageTabsKeys = {
	[PageTabs.General]: 'general',
	[PageTabs.SubPages]: 'pages',
	[PageTabs.Images]: 'images',
	[PageTabs.Videos]: 'videos',
	[PageTabs.Faqs]: 'faqs',
	[PageTabs.Cases]: 'cases',
	[PageTabs.Reviews]: 'reviews',
	[PageTabs.Specials]: 'specials',
	[PageTabs.Doctors]: 'doctors',
	[PageTabs.Menu]: 'menu',
	[PageTabs.DoctorsMenu]: 'doctor-menu',
};

export enum DoctorTabs {
	General = 0,
	WorkingHours = 1,
	ServiceHours = 2,
	// Prescriptions
	Sigs = 3,
	Pharmacies = 4,
	Compounds = 5,
	PrescriptionNotes = 6,
	PrescriptionSettings = 7,
	// WizardSettings
	Menu = 8,
	DoctorNotes = 9,
	WizardNotes = 10,
	Media = 11,
	//
	Questions = 12,
	Reminders = 13,
	Documents = 14,
	DayOff = 15,
	Finances = 16,
	// Settings
	SettingsGeneral = 17,
	ElectronicSignature = 18,
	ICD10 = 19,
	Insurance = 20,
	Site = 21,
	Portal = 22,
	Rooms = 23,
	DailyAgenda = 24,
	WorkingIntervals = 25,
	Reviews = 26,
	InfoCopying = 27,
	// working hours
	WorkingHoursGeneral = 28,
	WorkingHoursCustom = 29,
	// finances
	FinancesGeneral = 30,
	Commissions = 31,

}

export const DoctorTabsKeys = {
	[DoctorTabs.General]: 'general',

	[DoctorTabs.WorkingHours]: 'working-hours',
	[DoctorTabs.WorkingHoursGeneral]: 'general',
	[DoctorTabs.WorkingHoursCustom]: 'custom',

	[DoctorTabs.ServiceHours]: 'services',

	[DoctorTabs.Sigs]: 'sigs',
	[DoctorTabs.Pharmacies]: 'pharmacies',
	[DoctorTabs.Compounds]: 'compounds',
	[DoctorTabs.PrescriptionNotes]: 'notes',
	[DoctorTabs.PrescriptionSettings]: 'settings',

	[DoctorTabs.Menu]: 'menu',
	[DoctorTabs.DoctorNotes]: 'notes',
	[DoctorTabs.WizardNotes]: 'wizard-notes',
	[DoctorTabs.Media]: 'media',

	[DoctorTabs.Questions]: 'questions',
	[DoctorTabs.Reminders]: 'reminders',
	[DoctorTabs.Documents]: 'documents',
	[DoctorTabs.DayOff]: 'day-offs',

	[DoctorTabs.Finances]: 'finances',
	[DoctorTabs.FinancesGeneral]: 'general',
	[DoctorTabs.Commissions]: 'commissions',

	[DoctorTabs.SettingsGeneral]: 'general',
	[DoctorTabs.ElectronicSignature]: 'electronicSignature',
	[DoctorTabs.ICD10]: 'icd10',
	[DoctorTabs.Insurance]: 'insurance',
	[DoctorTabs.Site]: 'site',
	[DoctorTabs.Portal]: 'portal',
	[DoctorTabs.Rooms]: 'rooms',
	[DoctorTabs.DailyAgenda]: 'dailyAgenda',
	[DoctorTabs.WorkingIntervals]: 'workingIntervals',
	[DoctorTabs.Reviews]: 'reviews',
	[DoctorTabs.InfoCopying]: 'infoCopying',
};

export enum DoctorTabsGroup {
	Prescriptions = 0,
	WizardSettings = 1,
	Settings = 2,
	WorkingHours = 3,
	Finances = 4,
}

export const DoctorTabsGroupKeys = {
	[DoctorTabsGroup.Prescriptions]: 'prescriptions',
	[DoctorTabsGroup.WizardSettings]: 'wizard',
	[DoctorTabsGroup.Settings]: 'settings',
	[DoctorTabsGroup.WorkingHours]: 'working-hours',
	[DoctorTabsGroup.Finances]: 'finances',
};

export const DoctorTabsGroupInnerKeys = {
	[DoctorTabsGroup.Prescriptions]: 'prescriptions-tabs',
	[DoctorTabsGroup.WizardSettings]: 'wizard-settings',
	[DoctorTabsGroup.Settings]: 'settings-tabs',
	[DoctorTabsGroup.WorkingHours]: 'working-hours-tab',
	[DoctorTabsGroup.Finances]: 'finances-tab',
};

export const DoctorTabGroup = {
	[DoctorTabs.Sigs]: DoctorTabsGroup.Prescriptions,
	[DoctorTabs.Pharmacies]: DoctorTabsGroup.Prescriptions,
	[DoctorTabs.Compounds]: DoctorTabsGroup.Prescriptions,
	[DoctorTabs.PrescriptionNotes]: DoctorTabsGroup.Prescriptions,
	[DoctorTabs.PrescriptionSettings]: DoctorTabsGroup.Prescriptions,

	[DoctorTabs.Menu]: DoctorTabsGroup.WizardSettings,
	[DoctorTabs.DoctorNotes]: DoctorTabsGroup.WizardSettings,
	[DoctorTabs.WizardNotes]: DoctorTabsGroup.WizardSettings,
	[DoctorTabs.Media]: DoctorTabsGroup.WizardSettings,

	[DoctorTabs.SettingsGeneral]: DoctorTabsGroup.Settings,
	[DoctorTabs.ElectronicSignature]: DoctorTabsGroup.Settings,
	[DoctorTabs.ICD10]: DoctorTabsGroup.Settings,
	[DoctorTabs.Insurance]: DoctorTabsGroup.Settings,
	[DoctorTabs.Site]: DoctorTabsGroup.Settings,
	[DoctorTabs.Portal]: DoctorTabsGroup.Settings,
	[DoctorTabs.Rooms]: DoctorTabsGroup.Settings,
	[DoctorTabs.DailyAgenda]: DoctorTabsGroup.Settings,
	[DoctorTabs.WorkingIntervals]: DoctorTabsGroup.Settings,
	[DoctorTabs.Reviews]: DoctorTabsGroup.Settings,
	[DoctorTabs.InfoCopying]: DoctorTabsGroup.Settings,

	[DoctorTabs.WorkingHoursGeneral]: DoctorTabsGroup.WorkingHours,
	[DoctorTabs.WorkingHoursCustom]: DoctorTabsGroup.WorkingHours,

	[DoctorTabs.FinancesGeneral]: DoctorTabsGroup.Finances,
	[DoctorTabs.Commissions]: DoctorTabsGroup.Finances,
};

export enum ProductTabs {
	General = 0,
	Locations = 1,
	Commissions = 2,
	Procedures = 3,
	Invoices = 4,
}

export const ProductTabsKeys = {
	[ProductTabs.General]: 'general',
	[ProductTabs.Locations]: 'locations',
	[ProductTabs.Commissions]: 'commissions',
	[ProductTabs.Procedures]: 'procedures',
	[ProductTabs.Invoices]: 'invoices',
};

export enum ProcedureTabs {
	General = 0,
	ServiceHours = 1,
	Consents = 2,
	Instructions = 3,
	Questions = 4,
	Specials = 5,
	Products = 6,
}

export const ProcedureTabsKeys = {
	[ProcedureTabs.General]: 'general',
	[ProcedureTabs.ServiceHours]: 'service-hours',
	[ProcedureTabs.Consents]: 'consent-forms',
	[ProcedureTabs.Instructions]: 'instructions',
	[ProcedureTabs.Questions]: 'questionnaires',
	[ProcedureTabs.Specials]: 'specials',
	[ProcedureTabs.Products]: 'products',
};

export enum InquiryTabs {
	General = 0,
	InquiryProcessing = 1,
	Appointment = 2,
}

export const InquiryTabsKeys = {
	[InquiryTabs.General]: 'general',
	[InquiryTabs.InquiryProcessing]: 'inquiry-processing',
	[InquiryTabs.Appointment]: 'appointments',
};

interface LinkContext {
	navigate: NavigateFunction;
	location: any;
	checkUserGroupAccess: (userGroup: UserGroupType | Array<UserGroupType>, anyMatch?: boolean) => boolean;
}

interface Link {
	path: string;
	query: NumberKeyedDictionary<string>;
	context: LinkContext;
	tryPrev?: boolean;
}

export interface BaseLinkBuilder {
	link: () => To,
	toString: () => string;
	navigate: (options?: NavigateOptions | undefined) => void,
	add: (key: string, value: string) => BaseLinkBuilder;
	addIfHasValue: (key: string, value: any) => BaseLinkBuilder;
	getLink: () => Link;
	toEditorLink: () => EditorLinkBuilder;
	toAppointmentLink: () => AppointmentLinkBuilder;
}

export interface EditorLinkBuilder extends BaseLinkBuilder {
	mode: (mode: Mode) => BaseLinkBuilder;
}

export interface EditorWithTabsLinkBuilder<TabType, BuilderType> extends EditorLinkBuilder {
	tab: (tab?: TabType) => BuilderType;
}

interface PatientNotesLinkBuilder<TabType, BuilderType> extends EditorWithTabsLinkBuilder<TabType, BuilderType> {
	notes: (tab?: PatientNoteTabs) => EditorWithTabsLinkBuilder<TabType, BuilderType>;
}

interface PatientLinkBuilder extends PatientNotesLinkBuilder<PatientTabs,
	PatientNotesLinkBuilder<PatientTabs, PatientLinkBuilder>> { }

interface AppointmentLinkBuilder extends PatientNotesLinkBuilder<AppointmentTabs,
	PatientNotesLinkBuilder<AppointmentTabs, AppointmentLinkBuilder>> { }

interface WizardLinkBuilder extends BaseLinkBuilder {

}

export interface AnalysisLinkBuilder extends EditorWithTabsLinkBuilder<AnalysisTabs, EditorLinkBuilder> {
	patientId: (patientId: number | null | undefined) => AnalysisLinkBuilder;
	appointmentId: (appointmentId: number | null | undefined) => AnalysisLinkBuilder;
}

export interface UserLinkBuilder extends EditorWithTabsLinkBuilder<UserTabs, EditorLinkBuilder> { }

export interface LocationLinkBuilder extends EditorWithTabsLinkBuilder<LocationTabs, EditorLinkBuilder> { }

export interface DoctorLinkBuilder extends EditorWithTabsLinkBuilder<DoctorTabs, EditorLinkBuilder> { }

export interface InquiryLinkBuilder extends EditorWithTabsLinkBuilder<InquiryTabs, EditorLinkBuilder> { }

export interface ProductLinkBuilder extends EditorWithTabsLinkBuilder<ProductTabs, EditorLinkBuilder> { }

export interface ProcedureLinkBuilder extends EditorWithTabsLinkBuilder<ProcedureTabs, EditorLinkBuilder> { }

export interface PageLinkBuilder extends EditorWithTabsLinkBuilder<PageTabs, EditorLinkBuilder> {}

interface CalendarLinkBuilder extends BaseLinkBuilder {
	locationId: (locationId: number | null | undefined) => CalendarLinkBuilder;
	appointmentId: (appointmentId: number | null | undefined) => CalendarLinkBuilder;
	date: (date: number | null | undefined) => CalendarLinkBuilder;
}

interface DayOffLinkBuilder extends BaseLinkBuilder {
	filters: (filters: DayOffFilters) => DayOffLinkBuilder;
}

export interface LinkBuilder {
	current: (withSearch?: boolean) => BaseLinkBuilder;
	prev: (path?: string) => BaseLinkBuilder;
	any: (path: string) => BaseLinkBuilder;
	patient: (patientId: number) => PatientLinkBuilder;
	appointment: (patientId: number, appointmentId: number) => AppointmentLinkBuilder;
	analysis: (analysisId: number) => AnalysisLinkBuilder;
	user: (userId: number) => UserLinkBuilder;
	location: (locationId: number) => LocationLinkBuilder;
	doctor: (doctorId: number) => DoctorLinkBuilder;
	insurance: (insuranceId: number) => EditorLinkBuilder;
	invoice: (invoiceId: number) => EditorLinkBuilder;
	page: (pageId: number, pageType: PageType) => PageLinkBuilder;
	product: (productId: number) => ProductLinkBuilder;
	procedure: (procedureId: number) => ProcedureLinkBuilder;
	special: (specialId: number) => EditorLinkBuilder;
	sale: (saleId: number) => EditorLinkBuilder;
	inquiry: (inquiryId: number) => InquiryLinkBuilder;
	review: (reviewId: number) => EditorLinkBuilder;
	case: (caseId: number) => EditorLinkBuilder;
	wizard: (appointmentId: number) => WizardLinkBuilder;
	checkout: (appointmentId: number) => BaseLinkBuilder;
	calendar: () => CalendarLinkBuilder;
	dayOffs: () => DayOffLinkBuilder;
	tryPrev: () => Omit<LinkBuilder, 'tryPrev'>;
}

const ModeUrlKey = 'mode';

const ModeKeys = {
	[Mode.View]: 'view',
	[Mode.Edit]: 'edit',
};

const linkBuilder = (item: Link, props?: any): BaseLinkBuilder => {
	const { location, navigate } = item.context;

	const searchParams = (query: NumberKeyedDictionary<string>) =>
		Object.entries(query).map(([key, value]) => [key, String(value)].join('=')).join('&');

	const toString = (link: Link) => {
		let url = `/${link.path}`;
		const queryParams = searchParams(link.query);
		if (queryParams) {
			url += `?${queryParams}`;
		}
		return url;
	};

	const link = (link: Link) => ({ pathname: `/${link.path}`, search: searchParams(link.query) });

	return {
		link: () => link(item),
		toString: () => toString(item),
		navigate: (options?: NavigateOptions | undefined) => (item.tryPrev && location.state && location.state.prevPath
			? navigate(location.state.prevPath, options)
			: navigate(link(item), {
				state: {
					...location.state,
					prevPath: `${location.pathname}${location.search}${location.hash}`,
					...options?.state,
				},
				replace: location.pathname === `/${item.path}`,
				...options,
			} as NavigateOptions)),
		add: (key, value) => linkBuilder({
			...item,
			query: {
				...item.query,
				[key]: value,
			},
		}),
		addIfHasValue: (key, value) => linkBuilder(typeof value !== 'undefined' && value !== ''
			? {
				...item,
				query: {
					...item.query,
					[key]: Array.isArray(value) ? `[${value}]` : `${value}`,
				},
			} : item),
		getLink: () => item,
		toEditorLink: () => editorLinkBuilder(item),
		toAppointmentLink: () => appointmentLinkBuilder(item),
		...props,
	};
};

const editorLinkBuilder = (link: Link, props = {}, builder = linkBuilder): EditorLinkBuilder =>
	linkBuilder(link, {
		mode: (mode: Mode) => builder(typeof mode === 'undefined'
			? link
			: (linkBuilder(link).add(ModeUrlKey, ModeKeys[mode]).getLink())),
		...props,
	}) as EditorLinkBuilder;

export const PatientNotesTabsUrlKey = 'patient-notes-tabs';

const patientNotesLinkBuilder = <TabType, BuilderType>(link: Link, props = {}, builder = editorLinkBuilder):
	PatientNotesLinkBuilder<TabType, BuilderType> =>
	editorLinkBuilder(link, {
		notes: (tab?: PatientNoteTabs) => builder(typeof tab === 'undefined'
			? link
			: linkBuilder(link)
				.add(PatientNotesTabsUrlKey, PatientNoteTabsKeys[tab])
				.getLink()),
		...props,
	}) as PatientNotesLinkBuilder<TabType, BuilderType>;

export const PatientTabsUrlKey = 'patient-tabs';

const patientLinkBuilder = (link: Link): PatientLinkBuilder =>
	patientNotesLinkBuilder(link, {
		tab: (tab?: PatientTabs) => patientNotesLinkBuilder(typeof tab !== 'number'
			? link
			: (!link.context.checkUserGroupAccess(UserGroupType.PatientMenuGroups)
				? linkBuilder(link)
					.add(PatientTabsUrlKey, PatientTabsGroupKeys[PatientTabGroup[tab]])
					.add(PatientTabsGroupInnerKeys[PatientTabGroup[tab]], PatientTabsKeys[tab])
				: linkBuilder(link)
					.add(PatientTabsUrlKey, PatientTabsKeys[tab])
			).getLink()),
	}) as PatientLinkBuilder;

export const AppointmentTabsUrlKey = 'appointment-tabs';

const appointmentLinkBuilder = (link: Link): AppointmentLinkBuilder => {
	// todo need add more checks
	if (!link.path.startsWith(`${patientEditorPath}/`)) {
		throw 'invalid appointment link';
	}

	return linkBuilder(link, {
		tab: (tab?: AppointmentTabs) => patientNotesLinkBuilder(typeof tab === 'undefined'
			? link
			: linkBuilder(link)
				.add(AppointmentTabsUrlKey, AppointmentTabsKeys[tab])
				.getLink()),
	}) as AppointmentLinkBuilder;
};

const patientEditorPath = 'patient-editor';

export const userEditorTabsUrlKey = 'user-profile-tabs';

const userLinkBuilder = (link: Link): UserLinkBuilder => editorLinkBuilder(link, {
	tab: (tab: UserTabs) => editorLinkBuilder(typeof tab === 'undefined'
		? link
		: linkBuilder(link)
			.add(userEditorTabsUrlKey, UserTabsKeys[tab])
			.getLink()),
}) as UserLinkBuilder;

export const analysisEditorTabsUrlKey = 'analysis-tabs';

const analysisLinkBuilder = (link: Link): AnalysisLinkBuilder => editorLinkBuilder(link, {
	tab: (tab: UserTabs) => editorLinkBuilder(typeof tab === 'undefined'
		? link
		: linkBuilder(link)
			.add(analysisEditorTabsUrlKey, AnalysisTabsKeys[tab])
			.getLink()),
	patientId: (patientId?: number) => analysisLinkBuilder(linkBuilder(link)
		.addIfHasValue('patientId', `${patientId ?? ''}`).getLink()),
	appointmentId: (appointmentId?: number) => analysisLinkBuilder(linkBuilder(link)
		.addIfHasValue('appointmentId', `${appointmentId ?? ''}`).getLink()),
}) as AnalysisLinkBuilder;

export const locationEditorTabsUrlKey = 'location-tabs';

const locationLinkBuilder = (link: Link): LocationLinkBuilder => linkBuilder(link, {
	tab: (tab: LocationTabs) => editorLinkBuilder(typeof tab === 'undefined'
		? link
		: (LocationTabGroup[tab] !== undefined
			? linkBuilder(link)
				.add(locationEditorTabsUrlKey, LocationTabsGroupKeys[LocationTabGroup[tab]])
				.add(LocationTabsGroupInnerKeys[LocationTabGroup[tab]], LocationTabsKeys[tab])
			: linkBuilder(link)
				.add(locationEditorTabsUrlKey, LocationTabsKeys[tab])
		).getLink()),
}) as LocationLinkBuilder;

export const doctorEditorTabsUrlKey = 'doctor-tabs';

const doctorLinkBuilder = (link: Link): DoctorLinkBuilder => linkBuilder(link, {
	tab: (tab: DoctorTabs) => editorLinkBuilder(typeof tab === 'undefined'
		? link
		: (DoctorTabGroup[tab] !== undefined
			? linkBuilder(link)
				.add(doctorEditorTabsUrlKey, DoctorTabsGroupKeys[DoctorTabGroup[tab]])
				.add(DoctorTabsGroupInnerKeys[DoctorTabGroup[tab]], DoctorTabsKeys[tab])
			: linkBuilder(link)
				.add(doctorEditorTabsUrlKey, DoctorTabsKeys[tab])
		).getLink()),
}) as DoctorLinkBuilder;

const insuranceLinkBuilder = (link: Link): EditorLinkBuilder => editorLinkBuilder(link);

const invoiceLinkBuilder = (link: Link): EditorLinkBuilder => editorLinkBuilder(link);

export const productEditorTabsUrlKey = 'product-tabs';

const productLinkBuilder = (link: Link): ProductLinkBuilder => linkBuilder(link, {
	tab: (tab: ProductTabs) => editorLinkBuilder(typeof tab === 'undefined'
		? link
		: linkBuilder(link)
			.add(productEditorTabsUrlKey, ProductTabsKeys[tab])
			.getLink()),
}) as ProductLinkBuilder;

export const procedureEditorTabsUrlKey = 'procedure-tabs';

const procedureLinkBuilder = (link: Link): ProcedureLinkBuilder => linkBuilder(link, {
	tab: (tab: ProcedureTabs) => editorLinkBuilder(typeof tab === 'undefined'
		? link
		: linkBuilder(link)
			.add(procedureEditorTabsUrlKey, ProcedureTabsKeys[tab])
			.getLink()),
}) as ProcedureLinkBuilder;

export const pageEditorTabsUrlKey = 'page-tabs';
const pageLinkBuilder = (link: Link): PageLinkBuilder => linkBuilder(link, {
	tab: (tab: PageTabs) => editorLinkBuilder(typeof tab === 'undefined'
		? link
		: linkBuilder(link)
			.add(pageEditorTabsUrlKey, PageTabsKeys[tab])
			.getLink()),
}) as PageLinkBuilder;

const specialLinkBuilder = (link: Link): EditorLinkBuilder => editorLinkBuilder(link);

const saleLinkBuilder = (link: Link): EditorLinkBuilder => editorLinkBuilder(link);

export const inquiryEditorTabsUrlKey = 'inquiry-tabs';

const inquiryLinkBuilder = (link: Link): InquiryLinkBuilder => linkBuilder(link, {
	tab: (tab: InquiryTabs) => editorLinkBuilder(typeof tab === 'undefined'
		? link
		: linkBuilder(link)
			.add(inquiryEditorTabsUrlKey, InquiryTabsKeys[tab])
			.getLink()),
}) as InquiryLinkBuilder;

const reviewLinkBuilder = (link: Link): EditorLinkBuilder => editorLinkBuilder(link);

const caseLinkBuilder = (link: Link): EditorLinkBuilder => editorLinkBuilder(link);

const wizardLinkBuilder = (link: Link): WizardLinkBuilder => linkBuilder(link);

const checkoutLinkBuilder = (link: Link): BaseLinkBuilder => linkBuilder(link);

const calendarLinkBuilder = (link: Link): CalendarLinkBuilder => linkBuilder(link, {
	locationId: (locationId?: number) => calendarLinkBuilder(linkBuilder(link)
		.addIfHasValue('locationId', locationId).getLink()),
	appointmentId: (appointmentId?: number) => calendarLinkBuilder(linkBuilder(link)
		.addIfHasValue('appointmentId', appointmentId).getLink()),
	date: (date?: number) => calendarLinkBuilder(linkBuilder(link)
		.addIfHasValue('date', date).getLink()),
	/*
	filter: (filters: CalendarFilters) => linkBuilder(linkBuilder(link)
		.addIfHasValue('locationId', filters.locationId)
		.addIfHasValue('appointmentId', filters.appointmentId)
		.getLink()),
	 */
}) as CalendarLinkBuilder;

const dayOffLinkBuilder = (link: Link): DayOffLinkBuilder => linkBuilder(link, {
	filters: (filters: DayOffFilters) => linkBuilder(linkBuilder(link)
		.addIfHasValue('dayOffLocationId', filters.dayOffLocationId)
		.addIfHasValue('dayOffDoctorId', filters.dayOffDoctorId)
		.addIfHasValue('employeeId', filters.employeeId)
		.addIfHasValue('dayOffObjectType', filters.dayOffObjectType)
		.addIfHasValue('dayOffTypes', filters.dayOffTypes)
		.addIfHasValue('dayOffStatus', filters.dayOffStatus)
		.addIfHasValue('timeMin', filters.timeMin)
		.addIfHasValue('timeMax', filters.timeMax)
		.addIfHasValue('text', filters.text)
		.getLink()),
}) as DayOffLinkBuilder;

/*
 * linkBuilder hook, you can use it like that
 * const linkBuilder = useLinkBuilder();
 * linkBuilder
 *  .appointment(item.id, newAppointment.id)
 *  .tab(AppointmentTabs.Payments)
 *  .notes(PatientNoteTabs.Patient)
 *  .navigate();
 */
export const useLinkBuilder = (): LinkBuilder => {
	const location = useLocation();
	const { pathname, state, search } = location;
	const navigate = useNavigate();
	const { checkUserGroupAccess } = useUserGroupContext();

	const handleQuery = (path: string) => {
		if (path.indexOf('=') === -1) {
			return {};
		}

		return parseQuery(path);
	};

	const baseCreateLink = (path: string, tryPrev?: boolean): Link => ({
		path: path.replace(/\?.*$/, ''),
		query: handleQuery(path.includes('?') ? path.slice(path.indexOf('?') + 1) : path),
		context: { navigate, location, checkUserGroupAccess },
		tryPrev,
	});

	const getResultLinkBuilder = (tryPrev: boolean) => {
		const createLink = (path: string) => baseCreateLink(path, tryPrev);
		return {
			current: (withSearch?: boolean) => linkBuilder(createLink(`${pathname.slice(1)}${withSearch ? `?${search.replace('?', '')}` : ''}`)),
			prev: (path = '') => linkBuilder(createLink(state && state?.prevPath ? state?.prevPath : path)),
			any: (path) => linkBuilder(createLink(path)),
			patient: (patientId) => patientLinkBuilder(createLink(`${patientEditorPath}/${patientId}`)),
			appointment: (patientId, appointmentId) => appointmentLinkBuilder(
				linkBuilder(createLink(`${patientEditorPath}/${patientId}`))
					.add(PatientTabsUrlKey, `${appointmentId}`)
					.getLink(),
			),
			analysis: (analysisId) => analysisLinkBuilder(createLink(`analysis-editor/${analysisId}`)),
			user: (userId) => userLinkBuilder(createLink(`user-editor/${userId}`)),
			location: (locationId) => locationLinkBuilder(createLink(`location-editor/${locationId}`)),
			doctor: (doctorId) => doctorLinkBuilder(createLink(`doctor-editor/${doctorId}`)),
			insurance: (insuranceId) => insuranceLinkBuilder(createLink(`insurance-editor/${insuranceId}`)),
			invoice: (invoiceId) => invoiceLinkBuilder(createLink(`invoice-editor/${invoiceId}`)),
			page: (pageId, pageType) => pageLinkBuilder(createLink(`${pageType === PageType.SERVICE ? 'service-' : ''}page-editor/${pageId}`)),
			product: (productId) => productLinkBuilder(createLink(`product-editor/${productId}`)),
			procedure: (procedureId) => procedureLinkBuilder(createLink(`procedure-editor/${procedureId}`)),
			special: (specialId) => specialLinkBuilder(createLink(`specials-editor/${specialId}`)),
			sale: (saleId) => saleLinkBuilder(createLink(`sale-editor/${saleId}`)),
			inquiry: (inquiryId) => inquiryLinkBuilder(createLink(`inquiry-editor/${inquiryId}`)),
			review: (reviewId) => reviewLinkBuilder(createLink(`review-editor/${reviewId}`)),
			case: (caseId) => caseLinkBuilder(createLink(`case-editor/${caseId}`)),
			wizard: (appointmentId) => wizardLinkBuilder(createLink(`appointment-wizard/${appointmentId}`)),
			checkout: (appointmentId) => checkoutLinkBuilder(createLink(`checkout/${appointmentId}`)),
			calendar: () => calendarLinkBuilder(createLink('calendar')),
			//
			dayOffs: () => dayOffLinkBuilder(createLink('day-off-list')),
		} as Omit<LinkBuilder, 'tryPrev'>;
	};

	return {
		...getResultLinkBuilder(false),
		tryPrev: () => getResultLinkBuilder(true),
	} as LinkBuilder;
};
